import React, { useMemo, useState } from 'react';
import {
  ChatBubbleLeftRightIcon,
  XMarkIcon,
  PaperAirplaneIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { usePortfolioData } from '../context/PortfolioDataContext';

const SUGGESTIONS = [
  'What do you specialize in?',
  'Tell me about your AI projects',
  'How can I contact you?',
];

function buildKnowledge(data) {
  const { personalInfo, services, products, portfolio } = data;
  const featured = products.filter((p) => p.featured).map((p) => p.title).join(', ');
  const companies = portfolio.map((e) => e.company).join(', ');

  return [
    {
      keywords: ['special', 'skill', 'do', 'expert', 'stack', 'technology'],
      reply: `I specialize in ${services.map((s) => s.title.toLowerCase()).join(', ')}. ${personalInfo.description}`,
    },
    {
      keywords: ['ai', 'openai', 'chatgpt', 'machine', 'llm', 'bot'],
      reply: `I've built AI-powered products including ${featured}. Check the Projects section for live demos and GitHub links.`,
    },
    {
      keywords: ['project', 'work', 'portfolio', 'built', 'github'],
      reply: `Featured projects: ${featured}. Scroll to Projects or ask about a specific technology.`,
    },
    {
      keywords: ['experience', 'company', 'job', 'career', 'worked'],
      reply: `I've worked at ${companies}, building enterprise web applications over 6+ years.`,
    },
    {
      keywords: ['contact', 'email', 'hire', 'reach', 'message'],
      reply: 'You can reach me via the Contact section below, or connect on LinkedIn and GitHub from my social links.',
    },
    {
      keywords: ['who', 'name', 'about', 'introduce', 'background'],
      reply: `I'm ${personalInfo.name} — ${personalInfo.tagline}. ${personalInfo.description}`,
    },
    {
      keywords: ['location', 'where', 'based', 'city'],
      reply: `I'm based in ${personalInfo.location.city}, ${personalInfo.location.state}, ${personalInfo.location.country}.`,
    },
  ];
}

function findReply(message, knowledge) {
  const lower = message.toLowerCase();
  const match = knowledge.find((entry) =>
    entry.keywords.some((kw) => lower.includes(kw))
  );
  if (match) return match.reply;
  return "I can tell you about my skills, AI projects, experience, or how to get in touch. Try one of the suggested prompts!";
}

export default function PortfolioAssistant() {
  const { data } = usePortfolioData();
  const knowledge = useMemo(() => buildKnowledge(data), [data]);
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      text: `Hi! I'm ${data.personalInfo.name}'s portfolio assistant. Ask me about skills, AI projects, or how to connect.`,
    },
  ]);
  const [isTyping, setIsTyping] = useState(false);

  const sendMessage = (text) => {
    const trimmed = text.trim();
    if (!trimmed || isTyping) return;

    setMessages((prev) => [...prev, { role: 'user', text: trimmed }]);
    setInput('');
    setIsTyping(true);

    const reply = findReply(trimmed, knowledge);
    window.setTimeout(() => {
      setMessages((prev) => [...prev, { role: 'assistant', text: reply }]);
      setIsTyping(false);
    }, 600 + Math.min(reply.length * 8, 1200));
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full bg-primary-600 px-5 py-3 text-white shadow-lg shadow-primary-900/40 transition-all duration-300 hover:bg-primary-500 hover:shadow-primary-600/50 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 focus:ring-offset-gray-900 animate-glow"
        aria-expanded={isOpen}
        aria-label={isOpen ? 'Close portfolio assistant' : 'Open portfolio assistant'}
      >
        {isOpen ? (
          <XMarkIcon className="h-5 w-5" />
        ) : (
          <>
            <SparklesIcon className="h-5 w-5" />
            <span className="hidden sm:inline text-sm font-medium">Ask AI</span>
          </>
        )}
      </button>

      {isOpen && (
        <div className="fixed bottom-24 right-4 sm:right-6 z-50 w-[min(100vw-2rem,24rem)] rounded-2xl border border-gray-700/80 bg-gray-900/95 shadow-2xl backdrop-blur-xl overflow-hidden flex flex-col max-h-[min(70vh,32rem)] animate-fade-in">
          <div className="flex items-center gap-3 border-b border-gray-800 px-4 py-3 bg-gradient-to-r from-primary-900/40 to-transparent">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-600/30 border border-primary-500/30">
              <ChatBubbleLeftRightIcon className="h-5 w-5 text-primary-400" />
            </div>
            <div>
              <p className="text-sm font-semibold text-white">Portfolio AI</p>
              <p className="text-xs text-gray-400">Powered by your site data</p>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[12rem]">
            {messages.map((msg, i) => (
              <div
                key={`${msg.role}-${i}`}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-primary-600 text-white rounded-br-md'
                      : 'bg-gray-800 text-gray-200 border border-gray-700 rounded-bl-md'
                  }`}
                >
                  {msg.text}
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="flex justify-start">
                <div className="rounded-2xl rounded-bl-md bg-gray-800 border border-gray-700 px-4 py-3 flex gap-1">
                  <span className="typing-dot" />
                  <span className="typing-dot" style={{ animationDelay: '0.15s' }} />
                  <span className="typing-dot" style={{ animationDelay: '0.3s' }} />
                </div>
              </div>
            )}
          </div>

          <div className="px-3 pb-2 flex flex-wrap gap-2">
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => sendMessage(s)}
                className="text-xs px-2.5 py-1 rounded-full border border-gray-700 text-gray-400 hover:border-primary-600 hover:text-primary-300 transition-colors"
              >
                {s}
              </button>
            ))}
          </div>

          <form
            className="flex gap-2 border-t border-gray-800 p-3"
            onSubmit={(e) => {
              e.preventDefault();
              sendMessage(input);
            }}
          >
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about my work..."
              className="form-input flex-1 rounded-full py-2 text-sm !bg-gray-800 focus:ring-1"
              disabled={isTyping}
            />
            <button
              type="submit"
              disabled={!input.trim() || isTyping}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-600 text-white disabled:opacity-40 hover:bg-primary-500 transition-colors"
              aria-label="Send message"
            >
              <PaperAirplaneIcon className="h-4 w-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}